import React, { useState, useEffect } from 'react';
import { X, CurrencyCircleDollar, Warning, CheckCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';
import './CustomPaymentModal.css';

function formatCurrency(amount) {
  return `GHS ${amount.toLocaleString('en-GH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function CustomPaymentModal({ 
  isOpen, 
  onClose, 
  maxAmount, 
  minAmount = 0,
  defaultAmount,
  onConfirm,
  isLoading = false,
  title = 'Custom Payment Amount'
}) {
  const [amount, setAmount] = useState(defaultAmount || maxAmount);
  const [validated, setValidated] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setAmount(defaultAmount || maxAmount);
      setValidated(false);
      setError('');
    }
  }, [isOpen, maxAmount, defaultAmount]);

  const handleAmountChange = (e) => {
    let value = e.target.value;
    // Remove non-numeric characters except decimal point
    value = value.replace(/[^\d.]/g, '');
    
    // Prevent multiple decimal points
    const parts = value.split('.');
    if (parts.length > 2) {
      value = parts[0] + '.' + parts[1];
    }

    // Limit to 2 decimal places
    if (parts[1] && parts[1].length > 2) {
      value = parts[0] + '.' + parts[1].substring(0, 2);
    }

    const numValue = parseFloat(value) || 0;
    setAmount(numValue);
    validateAmount(numValue);
  };

  const validateAmount = (value) => {
    setError('');
    setValidated(false);

    if (value <= 0) {
      setError('Amount must be greater than zero');
      return;
    }

    if (value < minAmount) {
      setError(`Minimum payment is ${formatCurrency(minAmount)}`);
      return;
    }

    if (value > maxAmount) {
      setError(`Payment cannot exceed outstanding balance of ${formatCurrency(maxAmount)}`);
      return;
    }

    setValidated(true);
  };

  const handleQuickSelect = (value) => {
    if (value <= maxAmount && value > 0) {
      setAmount(value);
      validateAmount(value);
    }
  };

  const handlePayFull = () => {
    setAmount(maxAmount);
    validateAmount(maxAmount);
    setValidated(true);
  };

  const handlePayHalf = () => {
    const halfAmount = Math.round(maxAmount / 2 * 100) / 100;
    setAmount(halfAmount);
    validateAmount(halfAmount);
    setValidated(true);
  };

  const handleConfirm = async () => {
    validateAmount(amount);
    if (validated) {
      await onConfirm(amount);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay custom-payment-modal" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{title}</h3>
          <button 
            className="modal-close" 
            onClick={onClose}
            disabled={isLoading}
            aria-label="Close modal"
          >
            <X size={24} />
          </button>
        </div>

        <div className="modal-body">
          {/* Amount Info */}
          <div className="amount-info">
            <div className="info-item">
              <span className="info-label">Outstanding Balance</span>
              <span className="info-value">{formatCurrency(maxAmount)}</span>
            </div>
            <div className="info-divider">→</div>
            <div className="info-item">
              <span className="info-label">You can pay</span>
              <span className="info-value highlight">Up to {formatCurrency(maxAmount)}</span>
            </div>
          </div>

          {/* Amount Input */}
          <div className="amount-input-section">
            <label className="input-label">Enter Payment Amount</label>
            <div className="amount-input-wrapper">
              <span className="currency-symbol">GHS</span>
              <input
                type="text"
                value={amount}
                onChange={handleAmountChange}
                placeholder="0.00"
                className={`amount-input ${error ? 'error' : ''} ${validated ? 'valid' : ''}`}
                disabled={isLoading}
                autoFocus
              />
              {validated && <CheckCircle size={20} className="input-valid-icon" />}
              {error && <Warning size={20} className="input-error-icon" />}
            </div>

            {error && (
              <div className="input-error">
                <Warning size={16} />
                <span>{error}</span>
              </div>
            )}
          </div>

          {/* Quick Select Buttons */}
          <div className="quick-select-section">
            <span className="quick-select-label">Quick select:</span>
            <div className="quick-buttons">
              <button
                className="quick-btn"
                onClick={handlePayHalf}
                disabled={isLoading}
              >
                Half ({formatCurrency(Math.round(maxAmount / 2 * 100) / 100)})
              </button>
              <button
                className="quick-btn primary"
                onClick={handlePayFull}
                disabled={isLoading}
              >
                Full ({formatCurrency(maxAmount)})
              </button>
            </div>
          </div>

          {/* Payment Breakdown */}
          {amount > 0 && !error && (
            <div className="payment-breakdown">
              <div className="breakdown-item">
                <span>Payment Amount</span>
                <span className="breakdown-value">{formatCurrency(amount)}</span>
              </div>
              <div className="breakdown-divider"></div>
              <div className="breakdown-item">
                <span>Remaining Balance</span>
                <span className={`breakdown-value ${maxAmount - amount > 0 ? 'warning' : 'success'}`}>
                  {formatCurrency(Math.max(0, maxAmount - amount))}
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button
            className="btn btn-secondary"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleConfirm}
            disabled={!validated || isLoading}
          >
            {isLoading ? (
              <>
                <span className="spinner"></span>
                Processing...
              </>
            ) : (
              <>
                <CurrencyCircleDollar size={18} />
                Proceed to Payment
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CustomPaymentModal;
